import { LiquidGlassElement } from './liquid-glass';

export class LiquidGlassControlsElement extends HTMLElement {
  private panelEl: HTMLDivElement;
  private toggleBtn: HTMLButtonElement;
  private targetGlass: LiquidGlassElement | null = null;

  static get observedAttributes() {
    return ['target'];
  }

  constructor() {
    super();
    this.panelEl = document.createElement('div');
    this.panelEl.className = 'lg-controls-panel';

    this.toggleBtn = document.createElement('button');
    this.toggleBtn.className = 'lg-panel-toggle';
    this.toggleBtn.setAttribute('aria-label', 'Toggle controls');
    this.toggleBtn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
        <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
        <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
        <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
      </svg>
    `;
  }

  connectedCallback() {
    if (!this.contains(this.panelEl)) {
      this.appendChild(this.toggleBtn);
      this.appendChild(this.panelEl);
    }
    this.findTarget();
    this.renderUI();
    this.setupEvents();
  }

  attributeChangedCallback(_name: string, _oldVal: string, _newVal: string) {
    if (this.isConnected) {
      this.findTarget();
    }
  }

  private findTarget() {
    const targetId = this.getAttribute('target');
    if (targetId) {
      this.targetGlass = (document.getElementById(targetId) as unknown) as LiquidGlassElement;
    } else {
      this.targetGlass = (document.querySelector('liquid-glass') as unknown) as LiquidGlassElement;
    }
  }

  private setupEvents() {
    this.toggleBtn.addEventListener('click', () => {
      this.panelEl.classList.add('open');
      this.toggleBtn.classList.add('hidden');
    });
  }

  private renderUI() {
    const target = this.targetGlass;
    const w = target ? target.glassWidth : 300;
    const h = target ? target.glassHeight : 200;
    const r = target ? target.radius : 60;
    const thick = target ? target.thickness : 80;
    const bezel = target ? target.bezel : 60;
    const ior = target ? target.ior : 3.0;
    const blur = target ? target.blurAmount : 0;
    const specOp = target ? target.specularOpacity : 0;
    const specSat = target ? target.specularSaturation : 4;
    const shBlur = target ? target.shadowBlur : 20;
    const shSpread = target ? target.shadowSpread : -5;
    const tintOp = target ? target.tintOpacity : 0;
    const outerBlur = target ? target.outerShadowBlur : 24;

    this.panelEl.innerHTML = `
      <div class="lg-controls-header">
        <span class="lg-controls-title">Glass Controls</span>
        <button class="lg-close-btn" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" width="14" height="14">
            <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
      </div>
      <div class="lg-controls-scroll">
        <h2>Glass Shape</h2>
        <label>Width
          <input type="range" id="lg-ctrl-w" min="150" max="700" value="${w}" />
          <span class="value-display" id="lg-ctrl-w-val">${w}</span>
        </label>
        <label>Height
          <input type="range" id="lg-ctrl-h" min="100" max="800" value="${h}" />
          <span class="value-display" id="lg-ctrl-h-val">${h}</span>
        </label>
        <label>Border Radius
          <input type="range" id="lg-ctrl-r" min="10" max="100" value="${r}" />
          <span class="value-display" id="lg-ctrl-r-val">${r}</span>
        </label>

        <h2>Refraction</h2>
        <label>Thickness
          <input type="range" id="lg-ctrl-thick" min="10" max="200" value="${thick}" />
          <span class="value-display" id="lg-ctrl-thick-val">${thick}</span>
        </label>
        <label>Bezel Width
          <input type="range" id="lg-ctrl-bezel" min="2" max="60" value="${bezel}" />
          <span class="value-display" id="lg-ctrl-bezel-val">${bezel}</span>
        </label>
        <label>Refractive Index (IOR)
          <input type="range" id="lg-ctrl-ior" min="1.0" max="3.0" step="0.05" value="${ior}" />
          <span class="value-display" id="lg-ctrl-ior-val">${ior}</span>
        </label>

        <h2>Appearance</h2>
        <label>Blur
          <input type="range" id="lg-ctrl-blur" min="0" max="8" step="0.1" value="${blur}" />
          <span class="value-display" id="lg-ctrl-blur-val">${blur}</span>
        </label>
        <label>Specular Opacity
          <input type="range" id="lg-ctrl-spec-op" min="0" max="1" step="0.05" value="${specOp}" />
          <span class="value-display" id="lg-ctrl-spec-op-val">${specOp}</span>
        </label>
        <label>Specular Saturation
          <input type="range" id="lg-ctrl-spec-sat" min="0" max="12" step="1" value="${specSat}" />
          <span class="value-display" id="lg-ctrl-spec-sat-val">${specSat}</span>
        </label>

        <h2>Inner Shadow</h2>
        <label>Color
          <input type="color" id="lg-ctrl-sh-color" value="${target ? target.shadowColor : '#ffffff'}" />
        </label>
        <label>Blur
          <input type="range" id="lg-ctrl-sh-blur" min="0" max="40" value="${shBlur}" />
          <span class="value-display" id="lg-ctrl-sh-blur-val">${shBlur}</span>
        </label>
        <label>Spread
          <input type="range" id="lg-ctrl-sh-spread" min="-15" max="10" value="${shSpread}" />
          <span class="value-display" id="lg-ctrl-sh-spread-val">${shSpread}</span>
        </label>

        <h2>Glass Tint</h2>
        <label>Tint Color
          <input type="color" id="lg-ctrl-tint-color" value="${target ? target.tintColor : '#ffffff'}" />
        </label>
        <label>Tint Opacity
          <input type="range" id="lg-ctrl-tint-op" min="0" max="40" step="1" value="${tintOp}" />
          <span class="value-display" id="lg-ctrl-tint-op-val">${tintOp}%</span>
        </label>

        <h2>Outer Shadow</h2>
        <label>Blur
          <input type="range" id="lg-ctrl-outer-blur" min="0" max="50" value="${outerBlur}" />
          <span class="value-display" id="lg-ctrl-outer-blur-val">${outerBlur}</span>
        </label>
      </div>
    `;

    const closeBtn = this.panelEl.querySelector('.lg-close-btn');
    closeBtn?.addEventListener('click', () => {
      this.panelEl.classList.remove('open');
      this.toggleBtn.classList.remove('hidden');
    });

    this.bindInputs();
  }

  private bindInputs() {
    const bindInput = (id: string, prop: keyof LiquidGlassElement, fmt: (v: string) => string) => {
      const input = this.panelEl.querySelector(`#${id}`) as HTMLInputElement;
      const valDisplay = this.panelEl.querySelector(`#${id}-val`);
      if (!input) return;

      input.addEventListener('input', () => {
        if (valDisplay) valDisplay.textContent = fmt(input.value);
        if (this.targetGlass) {
          (this.targetGlass as any)[prop] = input.type === 'range' ? parseFloat(input.value) : input.value;
        }
      });
    };

    bindInput('lg-ctrl-w', 'glassWidth', (v) => v);
    bindInput('lg-ctrl-h', 'glassHeight', (v) => v);
    bindInput('lg-ctrl-r', 'radius', (v) => v);
    bindInput('lg-ctrl-thick', 'thickness', (v) => v);
    bindInput('lg-ctrl-bezel', 'bezel', (v) => v);
    bindInput('lg-ctrl-ior', 'ior', (v) => parseFloat(v).toFixed(2));
    bindInput('lg-ctrl-blur', 'blurAmount', (v) => parseFloat(v).toFixed(1));
    bindInput('lg-ctrl-spec-op', 'specularOpacity', (v) => parseFloat(v).toFixed(2));
    bindInput('lg-ctrl-spec-sat', 'specularSaturation', (v) => v);
    bindInput('lg-ctrl-sh-color', 'shadowColor', (v) => v);
    bindInput('lg-ctrl-sh-blur', 'shadowBlur', (v) => v);
    bindInput('lg-ctrl-sh-spread', 'shadowSpread', (v) => v);
    bindInput('lg-ctrl-tint-color', 'tintColor', (v) => v);
    bindInput('lg-ctrl-tint-op', 'tintOpacity', (v) => `${v}%`);
    bindInput('lg-ctrl-outer-blur', 'outerShadowBlur', (v) => v);
  }
}
